import type { PublicRuntimeConfig } from 'nuxt/schema';
import type { RouteLocationNormalized } from '#vue-router';
import { useMerchantSlug } from '~/composables/useMerchantSlug';

export type FeatureFlag = keyof PublicRuntimeConfig['features'];

export type FeatureFlags = Map<FeatureFlag, boolean>;

export interface FeatureFlagsState {
	features: FeatureFlags;
}

export default defineNuxtPlugin({
	name: 'feature-flags',
	setup: () => {
		const { public: { features: configFeatures } } = useRuntimeConfig();

		const FEATURE_FLAG_MERCHANT_OVERWRITES: Record<string, Partial<Record<FeatureFlag, boolean>>> = {
			thomann: {
				affiliatepage: true,
			},
			tonies: {
			},
			demo: {
				paidEngagementCampaigns: true,
				productWidget: true,
			},
			tisso: {
			},
		};

		const createFeatureStateFromConfig = () => {
			return new Map(Object.entries(configFeatures)) as FeatureFlags;
		};

		const createFeatureFlagsState = (): FeatureFlagsState => {
			const features = createFeatureStateFromConfig();

			return {
				features,
			};
		};

		const featureFlagsState = reactive(createFeatureFlagsState());

		const resetFeaturesToDefault = () => {
			featureFlagsState.features = new Map(Object.entries(configFeatures)) as FeatureFlags;
		};

		const overwriteFeaturesForMerchant = (merchantSlug: string) => {
			const merchantFeatureOverwrites = FEATURE_FLAG_MERCHANT_OVERWRITES[merchantSlug];

			if (!merchantFeatureOverwrites) {
				return;
			}

			Object.entries(merchantFeatureOverwrites).forEach(([feature, state]) => {
				featureFlagsState.features.set(feature as FeatureFlag, state);
			});
		};

		const routeQueryCache = new Map() as FeatureFlags;

		const overwriteFeaturesForRouteQuery = (route: RouteLocationNormalized) => {
			const routeFeatureOverwrites = Object
				.entries(route.query)
				.filter(([query]) => query.toLocaleLowerCase().startsWith('feature_'))
				.map(([query, value]) => ([query, Array.isArray(value) ? value[0] : value] as const))
				.reduce((object, [query, value]) => {
					const featureName = Object.keys(configFeatures).find((feature) => `feature_${feature.toLocaleLowerCase()}` === query.toLocaleLowerCase());

					if (!featureName) {
						return object;
					}

					return { ...object, [featureName]: value === 'true' };
				}, [] as Partial<Record<FeatureFlag, boolean>>);

			Object.entries(routeFeatureOverwrites).forEach(([feature, state]) => {
				routeQueryCache.set(feature as FeatureFlag, state);
			});

			featureFlagsState.features = new Map([...featureFlagsState.features, ...routeQueryCache]);
		};

		addRouteMiddleware('feature-flags', (to) => {
			resetFeaturesToDefault();

			const merchantSlug = useMerchantSlug(to);

			if (merchantSlug.value) {
				overwriteFeaturesForMerchant(merchantSlug.value);
			}

			if (to.query) {
				overwriteFeaturesForRouteQuery(to);
			}
		}, { global: true });

		return {
			provide: {
				featureFlags: featureFlagsState,
			},
		};
	},
});
